import { supabase } from '../supabase/client';
import { logActivity } from './activityService';
import type { CaseFilters, CreateCaseData, UpdateCaseData, SupportCaseInsert } from '@/types/case';

/**
 * Get cases with filters
 */
export const getCases = async (filters?: CaseFilters) => {
  try {
    let query = supabase
      .from('support_cases')
      .select(`
        *,
        customer:users!user_id(id, first_name, last_name, phone_number),
        assigned_to_staff:staff_users!assigned_to(id, first_name, last_name, email)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters?.priority && filters.priority !== 'all') {
      query = query.eq('priority', filters.priority);
    }

    if (filters?.case_type) {
      query = query.eq('case_type', filters.case_type);
    }

    if (filters?.assigned_to === 'me') {
      // Will be filtered in component based on current user
    } else if (filters?.assigned_to === 'unassigned') {
      query = query.is('assigned_to', null);
    } else if (filters?.assigned_to && filters.assigned_to !== 'all') {
      query = query.eq('assigned_to', filters.assigned_to);
    }

    const { data, error } = await query.limit(100);

    if (error) {
      console.error('[CaseService] Get cases error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get case by ID
 */
export const getCaseById = async (id: string) => {
  try {
    const { data, error } = await (supabase
      .from('support_cases')
      .select(`
        *,
        customer:users!user_id(id, first_name, last_name, phone_number, user_email),
        assigned_to_staff:staff_users!assigned_to(id, first_name, last_name, email, role),
        closed_by_staff:staff_users!closed_by(id, first_name, last_name),
        notes:case_notes(*, staff_user:staff_users(first_name, last_name))
      `)
      .eq('id', id)
      .single()) as any;

    if (error) {
      console.error('[CaseService] Get case error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create new case
 */
export const createCase = async (data: CreateCaseData, staffUserId: string) => {
  try {
    // Generate fingerprint
    const fingerprint = `${data.user_id}-${data.case_type}-${Date.now()}`;

    // Calculate SLA due date (4 hours for P1, 8 hours for P2, 24 hours for P3)
    const slaHours = data.priority === 'p1' ? 4 : data.priority === 'p2' ? 8 : 24;
    const slaDueAt = new Date();
    slaDueAt.setHours(slaDueAt.getHours() + slaHours);

    const caseData: SupportCaseInsert = {
      user_id: data.user_id,
      case_type: data.case_type,
      priority: data.priority,
      customer_message: data.customer_message || null,
      fingerprint,
      status: 'open',
      sla_due_at: slaDueAt.toISOString(),
      assigned_to: staffUserId,
      assigned_at: new Date().toISOString(),
    };

    const { data: caseRecord, error } = await supabase
      .from('support_cases')
      .insert(caseData as any)
      .select()
      .single() as any;

    if (error) {
      console.error('[CaseService] Create case error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'create_case',
      entity_type: 'case',
      entity_id: (caseRecord as any).id,
      details: { case_type: data.case_type, priority: data.priority },
    });

    // TODO: Send SMS if requested (will be implemented in SMS system)

    return { success: true, data: caseRecord };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update case
 */
export const updateCase = async (id: string, updates: UpdateCaseData, staffUserId: string) => {
  try {
    const { data, error } = await ((supabase
      .from('support_cases') as any)
      .update(updates)
      .eq('id', id)
      .select()
      .single());

    if (error) {
      console.error('[CaseService] Update case error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'update_case',
      entity_type: 'case',
      entity_id: id,
      details: updates,
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Assign case
 */
export const assignCase = async (caseId: string, staffUserId: string, assignerId: string) => {
  try {
    const { data, error } = await ((supabase
      .from('support_cases') as any)
      .update({
        assigned_to: staffUserId,
        assigned_at: new Date().toISOString(),
      })
      .eq('id', caseId)
      .select()
      .single());

    if (error) {
      console.error('[CaseService] Assign case error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: assignerId,
      action_type: 'assign_case',
      entity_type: 'case',
      entity_id: caseId,
      details: { assigned_to: staffUserId },
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Close case
 */
export const closeCase = async (id: string, resolution: string, staffUserId: string) => {
  try {
    const { data, error } = await ((supabase
      .from('support_cases') as any)
      .update({
        status: 'closed',
        resolution,
        closed_at: new Date().toISOString(),
        closed_by: staffUserId,
      })
      .eq('id', id)
      .select()
      .single());

    if (error) {
      console.error('[CaseService] Close case error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'close_case',
      entity_type: 'case',
      entity_id: id,
      details: { resolution },
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add case note
 */
export const addCaseNote = async (caseId: string, noteText: string, staffUserId: string, noteType: string = 'general') => {
  try {
    const { data, error } = await supabase
      .from('case_notes')
      .insert({
        case_id: caseId,
        staff_user_id: staffUserId,
        note_text: noteText,
        note_type: noteType as any,
        is_internal: true,
      } as any)
      .select()
      .single();

    if (error) {
      console.error('[CaseService] Add note error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'add_case_note',
      entity_type: 'case',
      entity_id: caseId,
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[CaseService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

export const caseService = {
  getCases,
  getCaseById,
  createCase,
  updateCase,
  assignCase,
  closeCase,
  addCaseNote,
};

